import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ShopMatchCard from "./ShopMatchCard";
import FilterChip from "./FilterChip";
import MapLegend from "./MapLegend";
import ShopSearchRecoveryPanel from "./ShopSearchRecoveryPanel";
import { buildShopSearchRecovery } from "./shopSearchRecovery";
import { buildShopMapPopupHtml, createShopMapMarker } from "./shopMapPopupCard";
import { createShopClusterer } from "./shopMapClusters";
import { buildShopPopupViewModel, getShopGoogleMapsUrl, shopOffersService } from "./shopDisplayHelpers";
import { SHOP_SERVICE_OPTIONS } from "../utils/shopServices";
import UseMyLocationButton from "./UseMyLocationButton";
import { mapStyleUrl } from "../utils/theme";

import { API_BASE } from "../utils/apiBase";
const DEFAULT_CENTER = [-98.5795, 39.8283]; // USA centroid-ish
const MAPBOX_JS_URL = "https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js";
const MAPBOX_CSS_URL = "https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css";
const RADIUS_SOURCE_ID = "shop-explorer-radius-source";
const RADIUS_FILL_LAYER_ID = "shop-explorer-radius-fill";
const RADIUS_OUTLINE_LAYER_ID = "shop-explorer-radius-outline";
const MOBILE_RADIUS_SOURCE_ID = "shop-explorer-mobile-radius-source";
const MOBILE_RADIUS_FILL_LAYER_ID = "shop-explorer-mobile-radius-fill";
const MOBILE_RADIUS_OUTLINE_LAYER_ID = "shop-explorer-mobile-radius-outline";
const MOBILE_RADIUS_COLOR = "#7c3aed";
const OTHER_MAKE = "__OTHER_MAKE__";
const RADIUS_OPTIONS = ["5", "10", "25", "50", "100", "250"];

function loadMapboxAssets() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  return new Promise((resolve, reject) => {
    const existingCss = document.querySelector(`link[href="${MAPBOX_CSS_URL}"]`);
    if (!existingCss) {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = MAPBOX_CSS_URL;
      document.head.appendChild(css);
    }

    const existingScript = document.querySelector(`script[src="${MAPBOX_JS_URL}"]`);
    if (existingScript) {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      existingScript.addEventListener("load", () => resolve(window.mapboxgl), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Mapbox script")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = MAPBOX_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error("Failed to load Mapbox script"));
    document.body.appendChild(script);
  });
}


function buildCircleGeoJson(center, radiusMiles, points = 64) {
  const [centerLon, centerLat] = center;
  const earthRadiusMiles = 3959;
  const angularDistance = radiusMiles / earthRadiusMiles;
  const centerLatRad = (centerLat * Math.PI) / 180;
  const centerLonRad = (centerLon * Math.PI) / 180;
  const coordinates = [];

  for (let i = 0; i <= points; i += 1) {
    const bearing = (2 * Math.PI * i) / points;
    const latRad = Math.asin(
      Math.sin(centerLatRad) * Math.cos(angularDistance) +
      Math.cos(centerLatRad) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const lonRad = centerLonRad + Math.atan2(
      Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(centerLatRad),
      Math.cos(angularDistance) - Math.sin(centerLatRad) * Math.sin(latRad)
    );

    coordinates.push([(lonRad * 180) / Math.PI, (latRad * 180) / Math.PI]);
  }

  return {
    type: "Feature",
    geometry: {
      type: "Polygon",
      coordinates: [coordinates],
    },
  };
}

// Draw violet service-area circles for the given mobile mechanics. Called with only the
// currently-unclustered shops on every moveend, so a mobile mechanic's radius shows when its
// pin is separated out and hides while it's inside a cluster. The source + layers are created
// once and then updated in place with setData, so the circles never flicker/repaint when the
// map settles (a remove/add teardown each moveend was causing a visible lag).
function renderMobileRadiusLayers(map, mobileShops) {
  if (!map || typeof map.getSource !== "function") return;

  const features = (mobileShops || [])
    .filter(
      (shop) =>
        shop.is_mobile_mechanic &&
        Number.isFinite(shop.latitude) &&
        Number.isFinite(shop.longitude) &&
        Number.isFinite(Number(shop.mobile_service_radius_miles))
    )
    .map((shop) => buildCircleGeoJson([shop.longitude, shop.latitude], Number(shop.mobile_service_radius_miles)));
  const data = { type: "FeatureCollection", features };

  const existing = map.getSource(MOBILE_RADIUS_SOURCE_ID);
  if (existing && typeof existing.setData === "function") {
    existing.setData(data);
    return;
  }
  map.addSource(MOBILE_RADIUS_SOURCE_ID, { type: "geojson", data });
  map.addLayer({
    id: MOBILE_RADIUS_FILL_LAYER_ID,
    type: "fill",
    source: MOBILE_RADIUS_SOURCE_ID,
    paint: { "fill-color": MOBILE_RADIUS_COLOR, "fill-opacity": 0.12 },
  });
  map.addLayer({
    id: MOBILE_RADIUS_OUTLINE_LAYER_ID,
    type: "line",
    source: MOBILE_RADIUS_SOURCE_ID,
    paint: { "line-color": MOBILE_RADIUS_COLOR, "line-width": 1.5, "line-dasharray": [2, 1] },
  });
}

export default function ShopExplorerPage({ token, onSessionExpired, userZipCode = "" }) {
  const runtimeToken =
    typeof window !== "undefined" && window.__ENV__ && typeof window.__ENV__.REACT_APP_MAPBOX_TOKEN === "string"
      ? window.__ENV__.REACT_APP_MAPBOX_TOKEN
      : "";
  const mapToken = runtimeToken || process.env.REACT_APP_MAPBOX_TOKEN || "";
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const clustererRef = useRef(null);
  const mapboxRef = useRef(null);
  const favoriteShopIdsRef = useRef([]);
  const fetchSeqRef = useRef(0);
  const activeFetchAbortRef = useRef(null);
  const lastMapFitKeyRef = useRef("");

  const [shops, setShops] = useState([]);
  const [searchDiagnostics, setSearchDiagnostics] = useState(null);
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapReady, setMapReady] = useState(false);
  const [zipSearchCenter, setZipSearchCenter] = useState(null);

  const [selectedMake, setSelectedMake] = useState("");
  const [otherMake, setOtherMake] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [radiusMiles, setRadiusMiles] = useState("5");
  const [filterGeneral, setFilterGeneral] = useState(false);
  const [filterFavoritesOnly, setFilterFavoritesOnly] = useState(false);
  const [filterService, setFilterService] = useState("");
  const [mobileFilter, setMobileFilter] = useState("include"); // include | only | hide
  const [sortBy, setSortBy] = useState("best_match");
  const [showSearchRadius, setShowSearchRadius] = useState(true);
  const [zipTouched, setZipTouched] = useState(false);
  const [favoriteShopIds, setFavoriteShopIds] = useState([]);
  const effectiveMake = selectedMake === OTHER_MAKE ? otherMake.trim() : selectedMake;

  useEffect(() => {
    favoriteShopIdsRef.current = favoriteShopIds;
  }, [favoriteShopIds]);

  useEffect(() => {
    const nextUserZip = String(userZipCode || "").trim();
    if (!zipTouched && nextUserZip) {
      setZipCode(nextUserZip);
    }
  }, [userZipCode, zipTouched]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/users/favorite-shops`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (!res.ok || !active) return;
        const data = await res.json();
        if (!active) return;
        setFavoriteShopIds(Array.isArray(data) ? data.map((x) => x.shop_user_id).filter(Boolean) : []);
      } catch {}
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const toggleFavoriteShop = useCallback(async (shopUserId, isFavoriteOverride = null) => {
    const isFavorite =
      typeof isFavoriteOverride === "boolean" ? isFavoriteOverride : favoriteShopIdsRef.current.includes(shopUserId);
    const res = await fetch(`${API_BASE}/users/favorite-shops${isFavorite ? `/${shopUserId}` : ""}`, {
      method: isFavorite ? "DELETE" : "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: isFavorite ? undefined : JSON.stringify({ shop_user_id: shopUserId }),
    });
    if (!res.ok) return;
    setFavoriteShopIds((prev) =>
      isFavorite ? prev.filter((id) => id !== shopUserId) : (prev.includes(shopUserId) ? prev : [...prev, shopUserId])
    );
    return !isFavorite;
  }, [token]);

  useEffect(() => {
    if (!mapToken || !mapContainerRef.current || mapRef.current) return undefined;

    let active = true;
    loadMapboxAssets()
      .then((mapboxgl) => {
        if (!active || !mapContainerRef.current || mapRef.current) return;
        mapboxRef.current = mapboxgl;
        mapboxgl.accessToken = mapToken;
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: mapStyleUrl(),
          center: DEFAULT_CENTER,
          zoom: 3,
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        if (typeof map.on === "function") {
          map.on("load", () => {
            if (active) setMapReady(true);
          });
        } else if (active) {
          setMapReady(true);
        }
        mapRef.current = map;
      })
      .catch(() => {
        if (active) setError("Failed to load Mapbox.");
      });

    return () => {
      active = false;
      clustererRef.current?.destroy();
      clustererRef.current = null;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, [mapToken]);

  const fetchShops = useCallback(async () => {
    if (activeFetchAbortRef.current) {
      activeFetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    activeFetchAbortRef.current = controller;
    const fetchSeq = ++fetchSeqRef.current;

    setLoading(true);
    setError("");
    setSearchDiagnostics(null);
    try {
      const params = new URLSearchParams();
      if (effectiveMake) params.set("make", effectiveMake);
      if (zipCode.trim()) {
        params.set("zip_code", zipCode.trim());
        params.set("radius_miles", radiusMiles);
      }

      const res = await fetch(`${API_BASE}/messages/shops/explorer?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: "no-store",
        signal: controller.signal,
      });
      if (res.status === 401 || res.status === 403) {
        if (onSessionExpired) onSessionExpired("/login");
        return;
      }
      if (fetchSeq !== fetchSeqRef.current) return;
      if (!res.ok) {
        setError("Failed to load shops.");
        setShops([]);
        setSearchDiagnostics(null);
        return;
      }
      const data = await res.json();
      if (fetchSeq !== fetchSeqRef.current) return;
      setShops(Array.isArray(data) ? data : []);

      if (zipCode.trim()) {
        try {
          const diagnosticsRes = await fetch(`${API_BASE}/messages/shops/search/diagnostics?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
            cache: "no-store",
            signal: controller.signal,
          });
          if (diagnosticsRes.ok) {
            const diagnosticsData = await diagnosticsRes.json();
            if (fetchSeq !== fetchSeqRef.current) return;
            setSearchDiagnostics(diagnosticsData && !Array.isArray(diagnosticsData) ? diagnosticsData : null);
          }
        } catch {
          if (!controller.signal.aborted) setSearchDiagnostics(null);
        }
      }
    } catch {
      if (controller.signal.aborted) return;
      if (fetchSeq !== fetchSeqRef.current) return;
      setError("Failed to load shops.");
      setShops([]);
      setSearchDiagnostics(null);
    } finally {
      if (fetchSeq !== fetchSeqRef.current) return;
      setLoading(false);
    }
  }, [effectiveMake, onSessionExpired, radiusMiles, token, zipCode]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/makes`, { cache: "no-store" });
        if (!res.ok || !active) return;
        const data = await res.json();
        if (!active) return;
        setMakes(Array.isArray(data) ? data : []);
      } catch {}
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchShops();
    }, 350);
    return () => clearTimeout(debounce);
  }, [fetchShops]);

  useEffect(() => {
    if (!zipCode.trim()) {
      setZipSearchCenter(null);
      return undefined;
    }

    const backendCenter = searchDiagnostics?.search_center;
    if (backendCenter && Number.isFinite(backendCenter.longitude) && Number.isFinite(backendCenter.latitude)) {
      setZipSearchCenter([backendCenter.longitude, backendCenter.latitude]);
      return undefined;
    }
    if (searchDiagnostics?.zip_found === false) {
      setZipSearchCenter(null);
      return undefined;
    }

    const controller = new AbortController();
    const loadZipCenter = async () => {
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            zipCode.trim()
          )}.json?country=us&types=postcode&limit=1&access_token=${encodeURIComponent(mapToken)}`,
          { signal: controller.signal }
        );
        if (!res.ok) {
          setZipSearchCenter(null);
          return;
        }
        const data = await res.json();
        const center = Array.isArray(data?.features) && data.features[0]?.center;
        if (
          Array.isArray(center) &&
          Number.isFinite(center[0]) &&
          Number.isFinite(center[1])
        ) {
          setZipSearchCenter([center[0], center[1]]);
        } else {
          setZipSearchCenter(null);
        }
      } catch {
        if (!controller.signal.aborted) {
          setZipSearchCenter(null);
        }
      }
    };

    loadZipCenter();
    return () => controller.abort();
  }, [zipCode, mapToken, searchDiagnostics]);

  useEffect(() => {
    return () => {
      if (activeFetchAbortRef.current) {
        activeFetchAbortRef.current.abort();
      }
    };
  }, []);

  const visibleShops = useMemo(() => {
    const hasMakeFilter = Boolean(effectiveMake);
    const filtered = shops.filter((shop) => {
      const favoriteMatch = !filterFavoritesOnly || favoriteShopIds.includes(shop.id);
      if (!favoriteMatch) return false;
      if (mobileFilter === "only" && !shop.is_mobile_mechanic) return false;
      if (mobileFilter === "hide" && shop.is_mobile_mechanic) return false;
      if (!shopOffersService(shop, filterService)) return false;
      if (!hasMakeFilter) return true;
      const specialistMatch = shop.is_specialist_for_make === true;
      const generalMatch = filterGeneral && shop.does_general_repair === true;
      return specialistMatch || generalMatch;
    });
    const sorted = [...filtered];
    if (sortBy === "distance") {
      sorted.sort((a, b) => (Number(a.distance_miles || Infinity) - Number(b.distance_miles || Infinity)));
    } else if (sortBy === "fast_response") {
      sorted.sort((a, b) => (Number(a.avg_first_response_minutes || Infinity) - Number(b.avg_first_response_minutes || Infinity)));
    } else {
      sorted.sort((a, b) => Number(b.fit_score || 0) - Number(a.fit_score || 0));
    }
    return sorted;
  }, [shops, effectiveMake, filterGeneral, filterFavoritesOnly, filterService, favoriteShopIds, sortBy, mobileFilter]);

  const searchRecovery = useMemo(() => {
    if (loading || !zipCode.trim()) return null;
    return buildShopSearchRecovery({
      shops,
      visibleShops,
      radiusMiles,
      radiusOptions: RADIUS_OPTIONS,
      make: effectiveMake,
      zipCode,
      filterGeneral,
      filterFavoritesOnly,
      diagnostics: searchDiagnostics,
    });
  }, [effectiveMake, filterFavoritesOnly, filterGeneral, loading, radiusMiles, searchDiagnostics, shops, visibleShops, zipCode]);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = mapboxRef.current;
    if (!map || !mapboxgl || !mapReady) return;

    try {
      // Lazily create the clusterer once the map is ready. It owns the shop pins and the
      // cluster bubbles and recomputes them on every moveend, and (via onRender) redraws the
      // mobile-radius circles for only the shops currently shown as individual pins.
      if (!clustererRef.current) {
        clustererRef.current = createShopClusterer({
          map,
          mapboxgl,
          onRender: (individualShops) => renderMobileRadiusLayers(map, individualShops),
        });
      }

      const valid = visibleShops.filter(
        (s) => Number.isFinite(s.latitude) && Number.isFinite(s.longitude)
      );
      const markerShops = Array.from(new Map(
        valid.map((shop, index) => [
          String(shop.shop_name || shop.id || `${shop.latitude}:${shop.longitude}:${index}`).trim().toLowerCase(),
          shop,
        ])
      ).values());

      // Builds one exact-anchor pin (with popup + favorite wiring) for an unclustered shop.
      const buildShopMarker = (shop) => {
        const popupView = buildShopPopupViewModel(shop, {
          effectiveMake,
          mapsUrl: getShopGoogleMapsUrl(shop),
          isFavorite: favoriteShopIds.includes(shop.id),
        });
        const popupHtml = buildShopMapPopupHtml({
          shop,
          mapsUrl: popupView.mapsUrl,
          badges: popupView.badges,
          effectiveMake,
          trustSummary: popupView.trustSummary,
          showFavoriteButton: true,
          isFavorite: popupView.isFavorite,
        });
        const marker = createShopMapMarker(mapboxgl, {
          label: shop.shop_name,
          variant: shop.is_mobile_mechanic ? "mobile" : "shop",
        })
          .setLngLat([shop.longitude, shop.latitude])
          .setPopup(new mapboxgl.Popup({ offset: 20, closeOnClick: false, maxWidth: "640px", className: "shop-map-popup shop-map-popup--flush-close shop-explorer-map-popup" }).setHTML(popupHtml))
          .addTo(map);
        marker.getPopup().on("open", () => {
          const popupEl = marker.getPopup()?.getElement();
          const favoriteButton = popupEl?.querySelector?.("[data-favorite-toggle='true']");
          if (favoriteButton) {
            const stopEvent = (event) => {
              event.preventDefault();
              event.stopPropagation();
            };
            favoriteButton.addEventListener("mousedown", stopEvent);
            favoriteButton.addEventListener("mouseup", stopEvent);
            favoriteButton.addEventListener("click", (event) => {
              stopEvent(event);
              const iconEl = favoriteButton.querySelector("i");
              const isFavoriteNow = !!iconEl && iconEl.className.includes("fi-sr-heart");
              toggleFavoriteShop(shop.id, isFavoriteNow).then((nextIsFavorite) => {
                if (!iconEl) return;
                iconEl.className = nextIsFavorite ? "fi fi-sr-heart" : "fi fi-rs-heart fi-rr-heart";
                favoriteButton.style.color = nextIsFavorite ? "#dc2626" : "#374151";
                favoriteButton.title = nextIsFavorite ? "Unfavorite shop" : "Favorite shop";
                favoriteButton.setAttribute("aria-label", nextIsFavorite ? "Unfavorite shop" : "Favorite shop");
              });
            });
          }
        });
        return marker;
      };

      // Clustering owns the shop pins + mobile-radius circles from here.
      clustererRef.current.setShops(markerShops, buildShopMarker);

      // Search-radius circle - independent of clustering/zoom.
      if (typeof map.getLayer === "function" && map.getLayer(RADIUS_FILL_LAYER_ID)) map.removeLayer(RADIUS_FILL_LAYER_ID);
      if (typeof map.getLayer === "function" && map.getLayer(RADIUS_OUTLINE_LAYER_ID)) map.removeLayer(RADIUS_OUTLINE_LAYER_ID);
      if (typeof map.getSource === "function" && map.getSource(RADIUS_SOURCE_ID)) map.removeSource(RADIUS_SOURCE_ID);
      if (showSearchRadius && zipSearchCenter && zipCode.trim() && Number.isFinite(Number(radiusMiles))) {
        const radiusFeature = buildCircleGeoJson(zipSearchCenter, Number(radiusMiles));
        map.addSource(RADIUS_SOURCE_ID, { type: "geojson", data: radiusFeature });
        map.addLayer({
          id: RADIUS_FILL_LAYER_ID,
          type: "fill",
          source: RADIUS_SOURCE_ID,
          paint: { "fill-color": "#2563eb", "fill-opacity": 0.14 },
        });
        map.addLayer({
          id: RADIUS_OUTLINE_LAYER_ID,
          type: "line",
          source: RADIUS_SOURCE_ID,
          paint: { "line-color": "#1d4ed8", "line-width": 2 },
        });
      }

      const bounds = new mapboxgl.LngLatBounds();
      let hasBounds = false;

      const fitShops = Array.from(new Map(
        shops
          .filter((shop) => Number.isFinite(shop.latitude) && Number.isFinite(shop.longitude))
          .map((shop, index) => [
            String(shop.shop_name || shop.id || `${shop.latitude}:${shop.longitude}:${index}`).trim().toLowerCase(),
            shop,
          ])
      ).values());
      fitShops.forEach((shop) => {
        bounds.extend([shop.longitude, shop.latitude]);
        hasBounds = true;
      });

      if (zipSearchCenter) {
        bounds.extend(zipSearchCenter);
        hasBounds = true;
      }

      if (!hasBounds) return undefined;

      const fitKey = JSON.stringify({
        shops: fitShops.map((shop) => [shop.id, shop.longitude, shop.latitude]),
        zipSearchCenter,
        zipCode: zipCode.trim(),
        radiusMiles,
        showSearchRadius,
      });
      if (lastMapFitKeyRef.current !== fitKey) {
        lastMapFitKeyRef.current = fitKey;
        map.fitBounds(bounds, { padding: 40, maxZoom: 10 });
      }
    } catch {
      setError("Failed to render map layers.");
    }
    return undefined;
  }, [visibleShops, shops, mapReady, effectiveMake, zipSearchCenter, zipCode, radiusMiles, showSearchRadius, favoriteShopIds, toggleFavoriteShop]);

  if (!mapToken) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="border rounded-xl px-3 py-2 text-sm border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-900 dark:text-yellow-200">
          Shop Explorer needs a Mapbox token. Set <code>REACT_APP_MAPBOX_TOKEN</code> in your frontend environment.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      <div className="border rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Shop Explorer</h3>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
          Browse registered shops on a map and apply shop filters used on car requests.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-600 dark:text-gray-300">Make (optional)</label>
          <select
            aria-label="Make (optional)"
            value={selectedMake}
            onChange={(e) => {
              setSelectedMake(e.target.value);
              if (e.target.value !== OTHER_MAKE) setOtherMake("");
            }}
            className="w-full p-2 border rounded"
          >
            <option value="">All makes</option>
            {makes.map((makeOption) => (
              <option key={makeOption} value={makeOption}>
                {makeOption}
              </option>
            ))}
            <option value={OTHER_MAKE}>Other…</option>
          </select>
          {selectedMake === OTHER_MAKE ? (
            <input
              value={otherMake}
              onChange={(e) => setOtherMake(e.target.value)}
              placeholder="Enter make"
              className="w-full p-2 border rounded mt-2"
            />
          ) : null}
        </div>
        <div>
          <label className="text-xs text-gray-600 dark:text-gray-300">ZIP (optional)</label>
          <input
            value={zipCode}
            inputMode="numeric"
            maxLength={5}
            onChange={(e) => {
              setZipTouched(true);
              setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5));
            }}
            placeholder="ZIP"
            aria-label="ZIP code"
            className="w-full p-2 border rounded"
          />
          <UseMyLocationButton
            className="mt-1"
            onZip={(z) => {
              setZipTouched(true);
              setZipCode(z);
            }}
          />
        </div>
        <div>
          <label className="text-xs text-gray-600 dark:text-gray-300">Radius miles</label>
          <select
            value={radiusMiles}
            onChange={(e) => setRadiusMiles(e.target.value)}
            className="w-full p-2 border rounded"
            disabled={!zipCode.trim()}
          >
            {RADIUS_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Filters auto-apply as you change selections.
      </p>
      {loading ? <p className="text-xs text-gray-500 dark:text-gray-400">Updating map…</p> : null}

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {zipCode.trim() ? (
            <FilterChip active={showSearchRadius} onToggle={() => setShowSearchRadius(!showSearchRadius)}>
              Show ZIP search radius
            </FilterChip>
          ) : null}
          {effectiveMake ? (
            <FilterChip active={filterGeneral} onToggle={() => setFilterGeneral(!filterGeneral)}>
              General auto repair
            </FilterChip>
          ) : (
            <span className="text-sm text-gray-600 dark:text-gray-300">Choose a make to focus specialist results.</span>
          )}
          <FilterChip active={filterFavoritesOnly} onToggle={() => setFilterFavoritesOnly(!filterFavoritesOnly)}>
            Favorites only
          </FilterChip>
          <span className="text-sm text-gray-600 dark:text-gray-300 sm:ml-auto">Visible shops: {visibleShops.length}</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
            <span>Mobile mechanics</span>
            <select
              aria-label="Mobile mechanics"
              value={mobileFilter}
              onChange={(e) => setMobileFilter(e.target.value)}
              className="w-full p-2 border rounded text-sm"
            >
              <option value="include">Include</option>
              <option value="only">Only</option>
              <option value="hide">Hide</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
            <span>Service</span>
            <select
              aria-label="Service (optional)"
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="w-full p-2 border rounded text-sm"
            >
              <option value="">Any service</option>
              {SHOP_SERVICE_OPTIONS.map((svc) => (
                <option key={svc} value={svc}>
                  {svc}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
            <span>Sort by</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full p-2 border rounded text-sm">
              <option value="best_match">Best Match</option>
              <option value="distance">Distance</option>
              <option value="fast_response">Fastest Response</option>
            </select>
          </label>
        </div>
      </div>

      {error ? (
        <div className="border rounded-xl px-3 py-2 text-sm border-red-300 bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300">{error}</div>
      ) : null}

      <div className="border rounded-xl overflow-hidden">
        <div ref={mapContainerRef} className="w-full h-[560px]" />
      </div>

      <MapLegend />

      <div className="border rounded-xl bg-white dark:bg-gray-800 p-4">
        <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3">Top matches</h4>
        <div className="space-y-3">
          {searchRecovery ? (
            <ShopSearchRecoveryPanel
              recovery={searchRecovery}
              onExpandRadius={(radius) => setRadiusMiles(radius)}
              onIncludeGeneral={() => setFilterGeneral(true)}
              onShowAll={() => setFilterFavoritesOnly(false)}
            />
          ) : null}
          {visibleShops.length > 5 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Showing the top 5 of {visibleShops.length} shops - see all of them on the map.
            </p>
          ) : null}
          {visibleShops.slice(0, 5).map((shop, idx) => (
            <ShopMatchCard
              key={shop.id}
              shop={shop}
              index={idx + 1}
              effectiveMake={effectiveMake}
              mapsUrl={getShopGoogleMapsUrl(shop)}
            />
          ))}
          {visibleShops.length === 0 ? <div className="text-sm text-gray-600 dark:text-gray-300">Adjust the search recovery options above or change your filters to show shops.</div> : null}
        </div>
      </div>
    </div>
  );
}
